import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { getClinicSubscriptionStatus } from '../../Services/APIs/appDetails';
import { getFlowDecision, groupOf } from '../../Utils/clinicFunnel';
import { isClinicAuthenticated } from '../../Utils/clinicAuth';
import FlowErrorCard from '../../Dashboard/Auth/FlowErrorCard';
import Loader from '../UI/Loader';

const RETRY_DELAYS_MS = [400, 1200];

const UNRESOLVED = { phase: 'loading', route: null, resolvedFrom: null };

const statusCodeOf = (error) => error?.response?.status ?? error?.status;

const isWorthRetrying = (error) => {
    const status = statusCodeOf(error);
    return status === undefined || status === 429 || status >= 500;
};

const isSessionDeath = (error) => statusCodeOf(error) === 401 || !isClinicAuthenticated();

const FunnelGate = () => {
    const location = useLocation();
    const group = groupOf(location.pathname);

    const callerClaimsResolved = Boolean(location.state?.funnelResolved);
    const hasValidatedSinceMountRef = useRef(false);

    const [decision, setDecision] = useState(UNRESOLVED);
    const retry = useCallback(() => setDecision(UNRESOLVED), []);

    const decisionCoversGroup = decision.phase !== 'loading'
        && (decision.resolvedFrom === group || decision.route === group);

    useEffect(() => {
        if (decisionCoversGroup) return undefined;

        const trustCaller = hasValidatedSinceMountRef.current && callerClaimsResolved;
        hasValidatedSinceMountRef.current = true;

        if (trustCaller) {
            setDecision({ phase: 'ready', route: group, resolvedFrom: group });
            return undefined;
        }

        let cancelled = false;

        (async () => {
            for (let i = 0; ; i++) {
                try {
                    const response = await getClinicSubscriptionStatus();
                    if (cancelled) return;
                    const { status, subscription_status } = response?.data?.data || {};
                    setDecision({
                        phase: 'ready',
                        route: getFlowDecision(status, subscription_status),
                        resolvedFrom: group,
                    });
                    return;
                } catch (error) {
                    if (cancelled) return;
                    if (isSessionDeath(error)) {
                        setDecision({ phase: 'sessionExpired', route: null, resolvedFrom: group });
                        return;
                    }
                    if (!isWorthRetrying(error) || i >= RETRY_DELAYS_MS.length) {
                        console.error('Error fetching subscription status:', error);
                        setDecision({ phase: 'unresolved', route: null, resolvedFrom: group });
                        return;
                    }
                    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[i]));
                    if (cancelled) return;
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [decisionCoversGroup, group, callerClaimsResolved]);

    if (!decisionCoversGroup) return <Loader />;

    if (decision.phase === 'sessionExpired') return null;

    if (decision.phase === 'unresolved') {
        if (group === '/dashboard') {
            return <FlowErrorCard
                variant="signin"
                message="We couldn't check your account status."
                onRetry={retry}
                isRetrying={false}
            />;
        }
        return <Outlet context={{ statusError: "Unable to check your subscription status.", retryStatus: retry }} />;
    }

    if (decision.route !== group) {
        return <Navigate to={decision.route} replace state={{ redirectedFrom: group }} />;
    }

    return <Outlet context={{ statusError: '', retryStatus: retry }} />;
};

export default FunnelGate;
